import { motion } from "motion/react";
import type { ReactNode } from "react";

/**
 * `contracts/design-system.md` § The shell: one row of the chrome's two cards, the workspace switcher
 * and the account card of the footer (`SidebarFooter.tsx`), taken from Spectrum UI's Nav List Card (`@spectrumui/nav-list-card`,
 * Apache-2.0) and themed through the variables of `packages/tokens` (2026-09-15, by the owner's
 * decision): a pill row with its icon and label that nudges 2 px on hover and presses in on tap, on
 * the source's spring. The danger tone is Profile Dropdown's sign-out row; `trailing` holds what the
 * row carries at its end, a check or a shortcut. The row is a button, because each row acts (switches,
 * opens, signs out) rather than naming a fixed destination.
 */
export interface NavCardRowProps {
  readonly icon?: ReactNode;
  readonly label: ReactNode;
  readonly onSelect: () => void;
  readonly tone?: "default" | "danger";
  readonly trailing?: ReactNode;
  /** The row is the one in effect, such as the current workspace. */
  readonly current?: boolean;
}

export function NavCardRow({ icon, label, onSelect, tone = "default", trailing, current = false }: NavCardRowProps) {
  return (
    <li>
      <button
        type="button"
        className="group/row block w-full cursor-pointer rounded-md text-start outline-none focus-visible:ring-(length:--focus-ring-width) focus-visible:ring-focus-ring"
        aria-current={current ? "true" : undefined}
        onClick={onSelect}
      >
        <motion.span
          whileHover={{ x: 2 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", bounce: 0.4, duration: 0.3 }}
          className={`flex min-h-(--control-height-sm) items-center gap-(--space-2) rounded-md px-(--space-3) text-sm transition-colors ${tone === "danger" ? "text-accent-red-text hover:bg-accent-red-bg" : "text-text-primary hover:bg-bg-hover group-aria-[current=true]/row:font-semibold"}`}
        >
          {icon !== undefined && (
            <span className="flex shrink-0 [&_svg]:size-(--icon-sm)" aria-hidden="true">
              {icon}
            </span>
          )}
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {trailing !== undefined && <span className="flex shrink-0 items-center text-text-tertiary [&_svg]:size-(--icon-sm)">{trailing}</span>}
        </motion.span>
      </button>
    </li>
  );
}
